/**
 * Vergleichstabelle des Blogs — reine Normalisierung von `tools-vergleich.json`.
 *
 * Die Datei wird redaktionell gepflegt (Preise ändern sich, Anbieter-Angaben
 * kommen nach), nicht von der Anwendung geschrieben. Sie wird deshalb wie
 * Fremd-Eingabe behandelt: fehlende Felder werden zu einem Gedankenstrich,
 * kaputtes JSON zu einer leeren Tabelle, und eine Website-Adresse muss http(s)
 * sein, bevor sie als Link ausgegeben wird. Ein `javascript:`-Wert aus einer
 * versehentlich falsch befüllten Zeile darf nicht als anklickbarer Link auf
 * einer öffentlichen Seite landen.
 */

/** Platzhalter für leere Felder (Vorgabe aus dem Umsetzungs-Briefing). */
export const EMPTY_FIELD = "—";

export interface ComparisonTool {
  id: string;
  name: string;
  logo: string;
  website: string;
  kategorie: string;
  datengrundlage: string;
  output: string;
  anbieter: string;
  zugang: string;
  preis: string;
  hervorheben: boolean;
}

export interface ToolComparison {
  preisstand: string;
  tools: ComparisonTool[];
}

function text(value: unknown): string {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed || EMPTY_FIELD;
}

/** Leerer Wert bleibt leer (kein Gedankenstrich) — für optionale Attribute. */
function optionalText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Nur absolute http(s)-Adressen werden zu Links. Alles andere (leer, relativ,
 * `javascript:`, `data:`) gibt einen leeren String — die Tabelle rendert den
 * Tool-Namen dann als reinen Text statt als Link.
 */
export function safeExternalUrl(value: unknown): string {
  const candidate = optionalText(value);
  if (!candidate) return "";
  try {
    const url = new URL(candidate);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : "";
  } catch {
    return "";
  }
}

/**
 * Nur repo-interne Bildpfade werden als Logo geladen. Solange die Logos aus
 * den offiziellen Brand-Bereichen der Anbieter fehlen, ist das Feld leer und
 * die Tabelle zeigt die Wortmarke.
 */
export function safeLogoPath(value: unknown): string {
  const candidate = optionalText(value);
  if (!candidate.startsWith("/") || candidate.startsWith("//")) return "";
  return /\.(?:svg|png|webp)$/i.test(candidate) ? candidate : "";
}

function normalizeTool(entry: unknown, index: number): ComparisonTool | null {
  if (!entry || typeof entry !== "object") return null;
  const raw = entry as Record<string, unknown>;
  const name = optionalText(raw.name);
  if (!name) return null;

  return {
    id: optionalText(raw.id) || `tool-${index + 1}`,
    name,
    logo: safeLogoPath(raw.logo),
    website: safeExternalUrl(raw.website),
    kategorie: text(raw.kategorie),
    datengrundlage: text(raw.datengrundlage),
    output: text(raw.output),
    anbieter: text(raw.anbieter),
    zugang: text(raw.zugang),
    preis: text(raw.preis),
    hervorheben: raw.hervorheben === true,
  };
}

/**
 * Rohes JSON → Tabellendaten. Die Reihenfolge der Datei bleibt erhalten; sie
 * ist redaktionell gesetzt und wird bewusst nicht sortiert.
 */
export function parseToolComparison(raw: string): ToolComparison {
  let parsed: unknown;
  try {
    parsed = JSON.parse(String(raw ?? ""));
  } catch {
    return { preisstand: "", tools: [] };
  }
  if (!parsed || typeof parsed !== "object") return { preisstand: "", tools: [] };

  const data = parsed as Record<string, unknown>;
  const list = Array.isArray(data.tools) ? data.tools : [];
  return {
    preisstand: optionalText(data.preisstand),
    tools: list.map(normalizeTool).filter((tool): tool is ComparisonTool => tool !== null),
  };
}
